// PatientDetails.js
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getOnePatient } from "../../service/api";
import { Card, Col, Container, Row, CardBody } from "reactstrap";
import jsPDF from "jspdf";
import 'jspdf-autotable';
const cardTopStyle = {
  backgroundColor: "#DC4C64",
  padding: "6px",
  color: "white",
  height: "30px",
}
export default function PatientDetails() {
  const { id } = useParams();
  const [patient, setPatient] = useState(null);

  const getDocumentUrl = (fileName = "") => {
    if (!fileName) return "";
    if (fileName.startsWith("http://") || fileName.startsWith("https://")) return fileName;
    return `https://oapi.ehrhub.co.za/uploads/${encodeURIComponent(fileName)}`;
    // return `http://localhost:9000/uploads/${encodeURIComponent(fileName)}`;
  };

  const getDocumentLabel = (fileName = "") => {
    const ext = fileName.includes(".") ? fileName.split(".").pop().toLowerCase() : "";
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "Image";
    if (ext === "pdf") return "PDF";
    if (["doc", "docx"].includes(ext)) return "Word";
    if (["xls", "xlsx", "csv"].includes(ext)) return "Excel";
    return "File";
  };

  useEffect(() => {
    const fetchPatient = async () => {
      try {
        const response = await getOnePatient(id);
        setPatient(response.data);
      } catch (error) {
        console.error("There was an error fetching the patient data!", error);
      }
    };

    fetchPatient();
  }, [id]);

  if (!patient) return <div>Loading...</div>;

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    // Theme colour matching #DC4C64
    const ACCENT = [220, 76, 100];
    const HEADER_TEXT = [255, 255, 255];
    const LABEL_COLOR = [100, 100, 100];
    const margin = 10;
    let currentY = 10;

    // ── Helpers ────────────────────────────────────────────────────────────
    const checkPageBreak = (needed = 20) => {
      if (currentY + needed > pageHeight - 15) {
        doc.addPage();
        currentY = 15;
      }
    };

    // Draw a coloured section title bar
    const drawSectionTitle = (title) => {
      checkPageBreak(12);
      doc.setFillColor(...ACCENT);
      doc.rect(margin, currentY, pageWidth - margin * 2, 8, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...HEADER_TEXT);
      doc.text(title, margin + 3, currentY + 5.5);
      doc.setTextColor(0, 0, 0);
      currentY += 10;
    };

    // Draw two side-by-side tables (left | right)
    const drawTableSideBySide = (leftRows, rightRows) => {
      const colW = (pageWidth - margin * 2) / 2 - 3;
      const filteredL = leftRows.filter(r => r[1] !== null && r[1] !== undefined && r[1] !== "");
      const filteredR = rightRows.filter(r => r[1] !== null && r[1] !== undefined && r[1] !== "");
      const startY = currentY;

      if (filteredL.length > 0) {
        doc.autoTable({
          startY,
          body: filteredL,
          theme: "grid",
          styles: { fontSize: 9.5, cellPadding: 2.5, textColor: [50, 50, 50] },
          columnStyles: {
            0: { fontStyle: "bold", cellWidth: 30, fillColor: [250, 250, 250], textColor: LABEL_COLOR },
            1: { cellWidth: "auto" },
          },
          tableWidth: colW,
          margin: { left: margin },
        });
      }
      const leftFinalY = filteredL.length > 0 ? doc.lastAutoTable.finalY : startY;

      if (filteredR.length > 0) {
        doc.autoTable({
          startY,
          body: filteredR,
          theme: "grid",
          styles: { fontSize: 9.5, cellPadding: 2.5, textColor: [50, 50, 50] },
          columnStyles: {
            0: { fontStyle: "bold", cellWidth: 30, fillColor: [250, 250, 250], textColor: LABEL_COLOR },
            1: { cellWidth: "auto" },
          },
          tableWidth: colW,
          margin: { left: margin * 2 + colW },
        });
      }
      const rightFinalY = filteredR.length > 0 ? doc.lastAutoTable.finalY : startY;

      currentY = Math.max(leftFinalY, rightFinalY) + 6;
    };

    // ── Build the PDF (after logo loads) ───────────────────────────────────
    const buildPDF = (base64Logo) => {

      // ── Page header ─────────────────────────────────────────────────────
      // Logo (top-right)
      if (base64Logo) {
        doc.addImage(base64Logo, "JPEG", pageWidth - 42, margin, 32, 20);
      }

      // Clinic info (below logo area)
      doc.setFontSize(9);
      doc.setTextColor(...LABEL_COLOR);
      let clinicY = margin;
      if (patient.clinicname?.name) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text(patient.clinicname.name, margin, clinicY + 5);
        clinicY += 8;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(...LABEL_COLOR);
        if (patient.clinicname.address) {
          // Split long addresses so they don't overlap Tel/Email below
          const addrLines = doc.splitTextToSize(patient.clinicname.address, pageWidth - margin * 2 - 44);
          doc.text(addrLines, margin, clinicY + 2);
          clinicY += addrLines.length * 5;
        }
        if (patient.clinicname.contactno) { doc.text(`Tel: ${patient.clinicname.contactno}`, margin, clinicY + 2); clinicY += 5; }
        if (patient.clinicname.email) { doc.text(`Email: ${patient.clinicname.email}`, margin, clinicY + 2); clinicY += 5; }
      }

      // Divider
      currentY = Math.max(clinicY + 6, base64Logo ? 38 : 28);
      doc.setDrawColor(...ACCENT);
      doc.setLineWidth(0.8);
      doc.line(margin, currentY, pageWidth - margin, currentY);
      currentY += 4;

      // Document title
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.setTextColor(...ACCENT);
      doc.text("PATIENT DEMOGRAPHIC FORM", pageWidth / 2, currentY + 6, { align: "center" });
      currentY += 8;

      // Printed on
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(...LABEL_COLOR);
      doc.text(`Printed On: ${new Date().toLocaleDateString()}`, pageWidth - margin, currentY + 4, { align: "right" });
      currentY += 8;

      doc.setTextColor(0, 0, 0);

      // ── Patient identity strip ───────────────────────────────────────────
      doc.setFillColor(245, 245, 245);
      doc.rect(margin, currentY, pageWidth - margin * 2, 14, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      const fullName = `${patient.title || ""} ${patient.firstname || ""} ${patient.lastname || ""}`.trim();
      doc.text(fullName, margin + 3, currentY + 6);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...LABEL_COLOR);
      doc.text(`MPI: ${patient.mpino || ""}`, margin + 3, currentY + 11);
      if (patient.age) doc.text(`Age: ${patient.age}`, margin + 60, currentY + 11);
      if (patient.gender) doc.text(`Gender: ${patient.gender}`, margin + 90, currentY + 11);
      if (patient.dob) doc.text(`DOB: ${patient.dob}`, margin + 130, currentY + 11);
      doc.setTextColor(0, 0, 0);
      currentY += 18;

      // ── Section 1: Personal Information ─────────────────────────────────
      drawSectionTitle("PERSONAL INFORMATION");
      drawTableSideBySide(
        [
          ["Full Name", fullName],
          ["Date of Birth", patient.dob],
          ["Age", patient.age],
          ["Gender", patient.gender],
          ["Nationality", patient.nationality],
          ["ID Number", patient.idnumber],
          ["Passport No.", patient.passportNumber],
          ["Expiry Date", patient.expiryDate],
        ],
        [
          ["Religion", patient.religion],
          ["Citizenship", patient.citizenship],
          ["Language", patient.language],
          ["Race", patient.race],
          ["Category", patient.patientcate],
          ["Classification", patient.sQuantityUnit.name],
          ["File No.", patient.fileno],
          ["Fund", patient.fund === "yes" ? "Yes - Has Medical Aid" : patient.fund === "no" ? "No" : ""],
        ]
      );

      // ── Section 2: Contact Information ──────────────────────────────────
      drawSectionTitle("CONTACT INFORMATION");
      drawTableSideBySide(
        [
          ["Mobile", patient.mobile],
          ["Alt. Number", patient.altNumber],
          ["Email", patient.email],
        ],
        [
          ["Address", patient.address],
          ["Suburb", patient.suburb],
          ["City", patient.city],
          ["Area Code", patient.acode],
          ["Region", patient.region],
          ["Ward", patient.ward],
        ]
      );

      // ── Section 3: Medical Aid (only if fund = yes) ──────────────────────
      if (patient.maidname || patient.mplan || patient.mfno) {
        drawSectionTitle("MEDICAL AID DETAILS");
        drawTableSideBySide(
          [
            ["Medical Aid", patient.maidname],
            ["Plan", patient.mplan],
            ["Fund Number", patient.mfno],
          ],
          [
            ["Amount", patient.mamount],
            ["Dependant Code", patient.mdepcode],
          ]
        );
      }

      // ── Section 4: Main Member (only if exists) ──────────────────────────
      const mm = patient.mainmemberdetail;
      if (mm?.mmfname) {
        checkPageBreak(30);
        drawSectionTitle("MAIN MEMBER DETAILS");
        const mmFullName = `${mm.mmtitle || ""} ${mm.mmfname || ""} ${mm.mmlname || ""}`.trim();

        // Build left + right field arrays
        const mmLeft = [
          ["Full Name", mmFullName],
          ["Nationality", mm.mmnationality],
          ["ID Number", mm.mmidnumber],
          ["Passport No.", mm.mmpassportNumber],
          ["Expiry Date", mm.mmexpiryDate],
          ["Date of Birth", mm.mmdob],
          ["Age", mm.mmage],
        ].filter(r => r[1] !== null && r[1] !== undefined && r[1] !== "");

        const mmRight = [
          ["Gender", mm.mmgender],
          ["Religion", mm.mmreligion],
          ["Citizenship", mm.mmcitizenship],
          ["Language", mm.mmlanguage],
          ["Race", mm.mmrace],
          ["Mobile", mm.mmmobile],
          ["Email", mm.mmemail],
          ["Alt. Number", mm.mmaltNumber],
        ].filter(r => r[1] !== null && r[1] !== undefined && r[1] !== "");

        // Combine into 4-column rows [label1, val1, label2, val2]
        // so the whole section is ONE table — page breaks work naturally
        const maxRows = Math.max(mmLeft.length, mmRight.length);
        const combined = [];
        for (let i = 0; i < maxRows; i++) {
          combined.push([
            mmLeft[i]?.[0] || "", mmLeft[i]?.[1] || "",
            mmRight[i]?.[0] || "", mmRight[i]?.[1] || "",
          ]);
        }

        doc.autoTable({
          startY: currentY,
          body: combined,
          theme: "grid",
          styles: { fontSize: 9, cellPadding: 2.5, textColor: [50, 50, 50] },
          columnStyles: {
            0: { fontStyle: "bold", cellWidth: 28, fillColor: [250, 250, 250], textColor: LABEL_COLOR },
            1: { cellWidth: "auto" },
            2: { fontStyle: "bold", cellWidth: 28, fillColor: [250, 250, 250], textColor: LABEL_COLOR },
            3: { cellWidth: "auto" },
          },
          margin: { left: margin, right: margin },
        });
        currentY = doc.lastAutoTable.finalY + 6;
      }

      // ── Section 5: Signature ─────────────────────────────────────────────
      if (patient?.signature) {
        checkPageBreak(45);
        drawSectionTitle("PATIENT SIGNATURE");
        const sigX = margin;
        const sigY = currentY;
        const sigW = 70;
        const sigH = 30;
        try {
          if (patient.signature.startsWith("data:image")) {
            doc.addImage(patient.signature, "PNG", sigX, sigY, sigW, sigH);
          } else {
            doc.addImage(`https://oapi.ehrhub.co.za/${patient.signature}`, "PNG", sigX, sigY, sigW, sigH);
          }
        } catch (_) {
          // Signature image failed to load — skip silently
        }
        currentY += sigH + 5;
      }

      // ── Footer on every page ────────────────────────────────────────────
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setDrawColor(...ACCENT);
        doc.setLineWidth(0.4);
        doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(...LABEL_COLOR);
        doc.text(`Confidential – Patient Record`, margin, pageHeight - 7);
        doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 7, { align: "right" });
      }

      doc.save(`Patient_${patient.mpino || patient._id}.pdf`);
    };

    // ── Load clinic logo then build ────────────────────────────────────────
    const loadImageAsBase64 = (imagePath, callback) => {
      if (!imagePath) { callback(null); return; }
      const xhr = new XMLHttpRequest();
      xhr.onload = function () {
        const reader = new FileReader();
        reader.onloadend = function () { callback(reader.result); };
        reader.readAsDataURL(xhr.response);
      };
      xhr.onerror = () => callback(null); // logo failed — still build PDF
      xhr.open("GET", imagePath);
      xhr.responseType = "blob";
      xhr.send();
    };

    let logoURL = null;
    if (patient?.clinicname?.logo) {
      const filename = patient.clinicname.logo.split("/").pop();
      logoURL = `https://oapi.ehrhub.co.za/uploads/${filename}`;
    }
    loadImageAsBase64(logoURL, buildPDF);
  };
  // Helper: render a label-value row used inside detail cards
  const InfoRow = ({ label, value, icon }) => {
    if (!value) return null;
    return (
      <div style={{ display: "flex", alignItems: "flex-start", padding: "7px 0", borderBottom: "1px solid #f1f1f1" }}>
        <span style={{ minWidth: "160px", color: "#74788d", fontSize: "13px", display: "flex", alignItems: "center", gap: "6px" }}>
          {icon && <i className={`mdi ${icon}`} style={{ color: "#DC4C64", fontSize: "14px" }}></i>}
          {label}
        </span>
        <span style={{ fontWeight: 500, color: "#343a40", fontSize: "13px" }}>{value}</span>
      </div>
    );
  };

  // Helper: section header bar (reuses existing cardTopStyle colour)
  const SectionHeader = ({ icon, title }) => (
    <div style={{ ...cardTopStyle, height: "36px", display: "flex", alignItems: "center", gap: "8px", borderRadius: "4px 4px 0 0", marginBottom: "0" }}>
      <i className={`mdi ${icon}`} style={{ fontSize: "16px" }}></i>
      <span style={{ fontWeight: 600, fontSize: "14px" }}>{title}</span>
    </div>
  );

  return (
    <div className="page-content">
      <Container fluid>

        {/* ── Page Header ─────────────────────────────────────────────── */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="page-title mb-0">Patient Profile</h4>
          <div style={{ display: "flex", gap: "8px" }}>
            <button className="btn btn-success" onClick={handleDownloadPDF} title="Download PDF">
              <i className="mdi mdi-download me-1"></i> PDF
            </button>
            <Link to="/Patient" className="btn btn-danger">
              <i className="mdi mdi-arrow-left me-1"></i> Back
            </Link>
          </div>
        </div>

        {/* ── Patient Identity Banner ──────────────────────────────────── */}
        <Card style={{ borderTop: "4px solid #DC4C64", marginBottom: "20px" }}>
          <CardBody>
            <Row className="align-items-center">

              {/* Avatar / Initials */}
              <Col xs="auto">
                <div style={{
                  width: "72px", height: "72px", borderRadius: "50%",
                  backgroundColor: "#DC4C64", color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "26px", fontWeight: 700, flexShrink: 0
                }}>
                  {(patient.firstname?.[0] || "").toUpperCase()}{(patient.lastname?.[0] || "").toUpperCase()}
                </div>
              </Col>

              {/* Name + key identifiers */}
              <Col>
                <h5 style={{ marginBottom: "2px", fontWeight: 700 }}>
                  {patient.title} {patient.firstname} {patient.lastname}
                </h5>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "6px" }}>
                  <span className="badge bg-danger" style={{ fontSize: "12px", padding: "5px 10px" }}>
                    <i className="mdi mdi-card-account-details me-1"></i>MPI: {patient.mpino}
                  </span>
                  {patient.patientcate && (
                    <span className="badge bg-secondary" style={{ fontSize: "12px", padding: "5px 10px" }}>
                      <i className="mdi mdi-account me-1"></i>{patient.patientcate}
                    </span>
                  )}
                  {patient.gender && (
                    <span className="badge bg-info" style={{ fontSize: "12px", padding: "5px 10px" }}>
                      <i className="mdi mdi-gender-male-female me-1"></i>{patient.gender}
                    </span>
                  )}
                  {patient.age && (
                    <span className="badge bg-warning text-dark" style={{ fontSize: "12px", padding: "5px 10px" }}>
                      <i className="mdi mdi-cake-variant me-1"></i>Age: {patient.age}
                    </span>
                  )}
                  {patient.fund === "yes" && (
                    <span className="badge bg-success" style={{ fontSize: "12px", padding: "5px 10px" }}>
                      <i className="mdi mdi-shield-check me-1"></i>Medical Aid
                    </span>
                  )}
                </div>
              </Col>

              {/* Practice info (top right) */}
              {patient.clinicname?.name && (
                <Col xs="auto" className="text-end d-none d-md-block">
                  <div style={{ fontSize: "12px", color: "#74788d" }}>Practice</div>
                  <div style={{ fontWeight: 600, color: "#343a40" }}>{patient.clinicname.name}</div>
                  {patient.clinicname.contactno && (
                    <div style={{ fontSize: "12px", color: "#74788d" }}>
                      <i className="mdi mdi-phone me-1"></i>{patient.clinicname.contactno}
                    </div>
                  )}
                </Col>
              )}
            </Row>
          </CardBody>
        </Card>

        {/* ── Row 1: Personal Info + Contact Info ─────────────────────── */}
        <Row className="mb-4">

          {/* Personal Details */}
          <Col md={6}>
            <SectionHeader icon="mdi-account-details" title="Personal Information" />
            <Card style={{ borderRadius: "0 0 4px 4px", marginBottom: 0 }}>
              <CardBody style={{ padding: "12px 18px" }}>
                <InfoRow label="Full Name" icon="mdi-account" value={`${patient.title || ""} ${patient.firstname || ""} ${patient.lastname || ""}`.trim()} />
                <InfoRow label="Date of Birth" icon="mdi-calendar" value={patient.dob} />
                <InfoRow label="Age" icon="mdi-numeric" value={patient.age} />
                <InfoRow label="Gender" icon="mdi-gender-male-female" value={patient.gender} />
                <InfoRow label="Nationality" icon="mdi-flag" value={patient.nationality} />
                <InfoRow label="ID Number" icon="mdi-card-account-details" value={patient.idnumber} />
                <InfoRow label="Passport No." icon="mdi-passport" value={patient.passportNumber} />
                <InfoRow label="Expiry Date" icon="mdi-calendar-clock" value={patient.expiryDate} />
                <InfoRow label="Religion" icon="mdi-hands-pray" value={patient.religion} />
                <InfoRow label="Citizenship" icon="mdi-earth" value={patient.citizenship} />
                <InfoRow label="Language" icon="mdi-translate" value={patient.language} />
                <InfoRow label="Race" icon="mdi-account-group" value={patient.race} />
                <InfoRow label="Classification" icon="mdi-tag" value={patient.sQuantityUnit.name} />
              </CardBody>
            </Card>
          </Col>

          {/* Contact Details */}
          <Col md={6}>
            <SectionHeader icon="mdi-phone-in-talk" title="Contact Information" />
            <Card style={{ borderRadius: "0 0 4px 4px", marginBottom: 0 }}>
              <CardBody style={{ padding: "12px 18px" }}>
                <InfoRow label="Mobile" icon="mdi-cellphone" value={patient.mobile} />
                <InfoRow label="Alt. Number" icon="mdi-phone-classic" value={patient.altNumber} />
                <InfoRow label="Email" icon="mdi-email" value={patient.email} />
                <InfoRow label="Address" icon="mdi-map-marker" value={patient.address} />
                <InfoRow label="Suburb" icon="mdi-home-city" value={patient.suburb} />
                <InfoRow label="City" icon="mdi-city" value={patient.city} />
                <InfoRow label="Area Code" icon="mdi-mailbox" value={patient.acode} />
                <InfoRow label="Region" icon="mdi-map" value={patient.region} />
                <InfoRow label="Ward" icon="mdi-home-map-marker" value={patient.ward} />
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ── Row 2: Medical Aid + Signature ──────────────────────────── */}
        <Row className="mb-4">

          {/* Medical Aid Details */}
          <Col md={6}>
            <SectionHeader icon="mdi-hospital-box" title="Medical Aid Details" />
            <Card style={{ borderRadius: "0 0 4px 4px", marginBottom: 0 }}>
              <CardBody style={{ padding: "12px 18px" }}>
                <InfoRow label="Medical Aid" icon="mdi-shield-cross" value={patient.maidname} />
                <InfoRow label="Plan" icon="mdi-file-document" value={patient.mplan} />
                <InfoRow label="Fund Number" icon="mdi-pound" value={patient.mfno} />
                <InfoRow label="Amount" icon="mdi-currency-usd" value={patient.mamount} />
                <InfoRow label="Dependant Code" icon="mdi-account-child" value={patient.mdepcode} />
              </CardBody>
            </Card>
          </Col>

          {/* Signature */}
          <Col md={6}>
            <SectionHeader icon="mdi-draw" title="Patient Signature" />
            <Card style={{ borderRadius: "0 0 4px 4px", marginBottom: 0 }}>
              <CardBody style={{ padding: "12px 18px", minHeight: "120px" }}>
                {patient.signature ? (
                  <img
                    src={patient.signature}
                    alt="Patient Signature"
                    style={{ maxWidth: "200px", border: "1px solid #dee2e6", borderRadius: "4px", padding: "6px" }}
                  />
                ) : (
                  <span style={{ color: "#74788d", fontSize: "13px" }}>No signature on record.</span>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {Array.isArray(patient.document_names) && patient.document_names.length > 0 && (
          <Row className="mb-4">
            <Col md={12}>
              <SectionHeader icon="mdi-file-multiple" title="Patient Documents" />
              <Card style={{ borderRadius: "0 0 4px 4px", marginBottom: 0 }}>
                <CardBody style={{ padding: "12px 18px" }}>
                  <Row>
                    {patient.document_names.map((docName, index) => (
                      <Col md={4} lg={3} key={`${docName}-${index}`} className="mb-3">
                        <a
                          href={getDocumentUrl(docName)}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            display: "block",
                            textDecoration: "none",
                            border: "1px solid #e9ecef",
                            borderRadius: 8,
                            padding: "12px",
                            background: "#fff",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: 8,
                                background: "#f8f0f2",
                                color: "#DC4C64",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 700,
                                fontSize: 11,
                                flexShrink: 0,
                              }}
                            >
                              {getDocumentLabel(docName)}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: "#343a40", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {docName}
                              </div>
                              <div style={{ fontSize: 12, color: "#74788d" }}>
                                Open document
                              </div>
                            </div>
                          </div>
                        </a>
                      </Col>
                    ))}
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* ── Main Member Details (only if data exists) ────────────────── */}
        {patient?.mainmemberdetail?.mmfname && (
          <Row className="mb-4">
            <Col md={12}>
              <SectionHeader icon="mdi-account-supervisor" title="Main Member Details" />
              <Card style={{ borderRadius: "0 0 4px 4px" }}>
                <CardBody style={{ padding: "12px 18px" }}>
                  <Row>
                    <Col md={6}>
                      <InfoRow label="Full Name" icon="mdi-account" value={`${patient.mainmemberdetail?.mmtitle || ""} ${patient.mainmemberdetail?.mmfname || ""} ${patient.mainmemberdetail?.mmlname || ""}`.trim()} />
                      <InfoRow label="Nationality" icon="mdi-flag" value={patient.mainmemberdetail?.mmnationality} />
                      <InfoRow label="ID Number" icon="mdi-card-account-details" value={patient.mainmemberdetail?.mmidnumber} />
                      <InfoRow label="Passport No." icon="mdi-passport" value={patient.mainmemberdetail?.mmpassportNumber} />
                      <InfoRow label="Expiry Date" icon="mdi-calendar-clock" value={patient.mainmemberdetail?.mmexpiryDate} />
                      <InfoRow label="Date of Birth" icon="mdi-calendar" value={patient.mainmemberdetail?.mmdob} />
                      <InfoRow label="Age" icon="mdi-numeric" value={patient.mainmemberdetail?.mmage} />
                    </Col>
                    <Col md={6}>
                      <InfoRow label="Gender" icon="mdi-gender-male-female" value={patient.mainmemberdetail?.mmgender} />
                      <InfoRow label="Religion" icon="mdi-hands-pray" value={patient.mainmemberdetail?.mmreligion} />
                      <InfoRow label="Citizenship" icon="mdi-earth" value={patient.mainmemberdetail?.mmcitizenship} />
                      <InfoRow label="Language" icon="mdi-translate" value={patient.mainmemberdetail?.mmlanguage} />
                      <InfoRow label="Race" icon="mdi-account-group" value={patient.mainmemberdetail?.mmrace} />
                      <InfoRow label="Mobile" icon="mdi-cellphone" value={patient.mainmemberdetail?.mmmobile} />
                      <InfoRow label="Email" icon="mdi-email" value={patient.mainmemberdetail?.mmemail} />
                      <InfoRow label="Alt. Number" icon="mdi-phone-classic" value={patient.mainmemberdetail?.mmaltNumber} />
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

      </Container>
    </div>
  );
}
